/* eslint-disable brace-style */

import { Injectable, Injector, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { authConfig } from './auth-config';
import { LoggingService } from '../services/logging.service';
import { SeverityLevel } from "@microsoft/applicationinsights-web";

@Injectable({ providedIn: 'root' })
export class AuthenticationService implements OnDestroy {

  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

  private isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();
  private router: Router
  private loggingService: LoggingService;
  private ngUnsubscribe = new Subject<void>();

  /**
   * Publishes `true` if and only if (a) all the asynchronous initial
   * login calls have completed or errorred, and (b) the user ended up
   * being authenticated.
   *
   * In essence, it combines:
   *
   * - the latest known state of whether the user is authorized
   * - whether the ajax calls for initial log in have all been done
   */
  public canActivateProtectedRoutes$: Observable<boolean> = combineLatest([
    this.isAuthenticated$,
    this.isDoneLoading$
  ]).pipe(map(values => values.every(b => b)));

  private navigateToLoginPage() {
    this.login();
  }

  constructor(
    private oauthService: OAuthService,
    private injector: Injector
  ) {
    this.loggingService = this.injector.get(LoggingService);
    setTimeout(() => {
      this.router = this.injector.get(Router);
    }, 100);
    // This is tricky, as it might cause race conditions (where access_token is set in another
    // tab before everything is said and done there.
    // TODO: Improve this setup. See: https://github.com/jeroenheijmans/sample-angular-oauth2-oidc-with-auth-guards/issues/2
    // window.addEventListener('storage', (event) => {
    //   // The `key` is `null` if the event was caused by `.clear()`
    //   if (event.key !== 'access_token' && event.key !== null) {
    //     return;
    //   }

    //   console.warn('Noticed changes to access_token (most likely from another tab), updating isAuthenticated');
    //   setTimeout(() => {
    //     this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());

    //     if (!this.oauthService.hasValidAccessToken()) {
    //       this.navigateToLoginPage();
    //     }
    //   }, 500);
    // });

    this.oauthService.events
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(_ => {
        this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
      });

    this.oauthService.events
      .pipe(takeUntil(this.ngUnsubscribe), filter(e => ['token_received'].includes(e.type)))
      .subscribe(_ => this.oauthService.loadUserProfile());

    this.oauthService.events
      .pipe(takeUntil(this.ngUnsubscribe), filter(e => ['session_terminated', 'session_error'].includes(e.type)))
      .subscribe(_ => this.navigateToLoginPage());

    this.oauthService.events
      .pipe(takeUntil(this.ngUnsubscribe), filter(e => ['silent_refresh_error'].includes(e.type)))
      .subscribe(event => {
        this.navigateToLoginPage();
        this.loggingService.trackException({
          exception: new Error(JSON.stringify(event)),
          severityLevel: SeverityLevel.Error,
        });
      });


    this.oauthService.setupAutomaticSilentRefresh();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public runInitialLoginSequence(): Promise<void> {
    this.oauthService.configure(authConfig);
    if (location.hash) {
      console.log('Encountered hash fragment, plotting as table...');
      console.table(location.hash.substr(1).split('&').map(kvp => kvp.split('=')));
    }

    // 0. LOAD CONFIG:
    // First we have to check to see how the IdServer is
    // currently configured:
    return this.oauthService.loadDiscoveryDocument()

      // 1. HASH LOGIN:
      // Try to log in via hash fragment after redirect back
      // from IdServer from initImplicitFlow:
      .then(() => this.oauthService.tryLogin())

      .then(() => {
        if (this.oauthService.hasValidAccessToken()) {
          return Promise.resolve();
        }

        // 2. SILENT LOGIN:
        // Try to log in via a refresh because then we can prevent
        // needing to redirect the user:
        return this.oauthService.silentRefresh()
          .then(() => Promise.resolve())
          .catch(result => {
            // Subset of situations from https://openid.net/specs/openid-connect-core-1_0.html#AuthError
            // Only the ones where it's reasonably sure that sending the
            // user to the IdServer will help.
            const errorResponsesRequiringUserInteraction = [
              'interaction_required',
              'login_required',
              'account_selection_required',
              'consent_required',
            ];

            if (result
              && result.reason
              && errorResponsesRequiringUserInteraction.indexOf(result.reason.error) >= 0) {

              // 3. ASK FOR LOGIN:
              // At this point we know for sure that we have to ask the
              // user to log in, so we redirect them to the IdServer to
              // enter credentials.
              //
              // Enable this to ALWAYS force a user to login.
              this.login();
            }

            // We can't handle the truth, just pass on the problem to the
            // next handler.
            return Promise.reject(result);
          });
      })

      .then(() => {
        this.isDoneLoadingSubject$.next(true);

        // Check for the strings 'undefined' and 'null' just to be sure. Our current
        // login(...) should never have this, but in case someone ever calls
        // initImplicitFlow(undefined | null) this could happen.
        if (this.oauthService.state && this.oauthService.state !== 'undefined' && this.oauthService.state !== 'null') {
          let stateUrl = this.oauthService.state;
          if (stateUrl.startsWith('/') === false) {
            stateUrl = decodeURIComponent(stateUrl);
          }
          console.log(`There was state of ${this.oauthService.state}, so we are sending you to: ${stateUrl}`);
          this.router.navigateByUrl(stateUrl);
        }
      })
      .catch(() => this.isDoneLoadingSubject$.next(true));
  }

  public login(targetUrl?: string) {
    // Note: before version 9.1.0 of the library you needed to
    // call encodeURIComponent on the argument to the method.
    this.oauthService.initLoginFlow(targetUrl || this.router.url);
  }

  public logout() { this.oauthService.logOut(); }
  public refresh() { this.oauthService.silentRefresh(); }
  public hasValidToken() { return this.oauthService.hasValidAccessToken(); }


  public accessToken() {
    return new Promise<string>(resolve => {
      resolve(this.oauthService.getAccessToken())
    });
  }

  public getIdToken() {
    return new Promise<string>(resolve => {
      resolve(this.oauthService.getIdToken())
    });
  }

  // These normally won't be exposed from a service like this, but
  // for debugging it makes sense.
  public get accessToken2() { return this.oauthService.getAccessToken(); }
  public get refreshToken() { return this.oauthService.getRefreshToken(); }
  public get identityClaims() { return this.oauthService.getIdentityClaims(); }
  public get idToken() { return this.oauthService.getIdToken(); }
  public get logoutUrl() { return this.oauthService.logoutUrl; }
}
